// engagement-components/AnalyticsPage.jsx — upgraded with DateRangePill + DownloadMenu
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Table, Badge, Spinner } from "react-bootstrap";
import { FaWhatsapp, FaSms, FaEnvelope } from "react-icons/fa";
import axios from "axios";
import moment from "moment";
import { apiUrl, authHeaders, SAMPLE } from "./config";
import { EngagementDatePicker, DownloadMenu, useNotify } from "./shared";

const FUNNEL_COLORS = ["#0d6efd","#0dcaf0","#198754","#ffc107","#dc3545"];

const AnalyticsPage = () => {
  const notify = useNotify();
  const [loading,      setLoading]      = useState(true);
  const [funnel,       setFunnel]       = useState([]);
  const [channelStats, setChannelStats] = useState([]);
  const [journeyStats, setJourneyStats] = useState([]);
  const [dateRange,    setDateRange]    = useState({
    start: moment().subtract(30,"days").startOf("day"),
    end:   moment().endOf("day"),
  });

  useEffect(() => { fetchAnalytics(); }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/analytics?from=${dateRange.start.format("YYYY-MM-DD")}&to=${dateRange.end.format("YYYY-MM-DD")}`, authHeaders());
      setFunnel(res.data.funnel || []);
      setChannelStats(res.data.channels || []);
      setJourneyStats(res.data.journeys || []);
    } catch {
      setFunnel([]);
      setChannelStats([]);
      setJourneyStats([]);
    } finally {
      setLoading(false);
    }
  };

  const maxFunnel = funnel[0]?.count || 1;

  const channelExportHeaders = [
    { key:"channel", label:"Channel" }, { key:"sent", label:"Sent" },
    { key:"delivered", label:"Delivered" }, { key:"clicked", label:"Clicked" },
    { key:"converted", label:"Converted" }, { key:"revenue", label:"Revenue (₹)" },
  ];
  const journeyExportHeaders = [
    { key:"name", label:"Journey" }, { key:"trigger", label:"Trigger" },
    { key:"sent", label:"Sent" }, { key:"ctr", label:"CTR %" },
    { key:"conversion", label:"Conv. %" }, { key:"revenue", label:"Revenue (₹)" },
    { key:"status", label:"Status" },
  ];

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <i className="fas fa-chart-bar text-primary me-2"></i>Analytics
            </h3>
            <p className="text-muted mb-0 mt-1">Engagement performance insights</p>
          </div>
          <EngagementDatePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onChange={({ start, end }) => setDateRange({ start, end })}
            align="right"
          />
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col lg={5}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><i className="fas fa-filter text-primary me-2"></i>Engagement Funnel</h6>
            </Card.Header>
            <Card.Body className="p-4">
              {funnel.length === 0 ? (
                <div style={{ textAlign:"center", padding:"32px 0", color:"#9ca3af" }}>
                  <i className="fas fa-chart-bar" style={{ fontSize:"2rem", marginBottom:10, display:"block" }} />
                  <div style={{ fontSize:"0.86rem", fontWeight:600, marginBottom:4 }}>No data yet</div>
                  <div style={{ fontSize:"0.78rem" }}>Send campaigns to see funnel data here</div>
                </div>
              ) : funnel.map((item, i) => (
                <div key={i} className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <small className="fw-semibold text-dark">{item.stage}</small>
                    <small>
                      <strong style={{ color: FUNNEL_COLORS[i] }}>{item.count.toLocaleString()}</strong>
                      {i > 0 && <span className="text-muted ms-1">({((item.count/maxFunnel)*100).toFixed(1)}%)</span>}
                    </small>
                  </div>
                  <div className="progress" style={{ height:10, borderRadius:5 }}>
                    <div className="progress-bar" style={{ width:`${(item.count/maxFunnel)*100}%`, background:FUNNEL_COLORS[i], borderRadius:5 }} />
                  </div>
                </div>
              ))}
              {funnel.length > 0 && null /* closed */}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0"><i className="fas fa-broadcast-tower text-primary me-2"></i>Channel Performance</h6>
              <DownloadMenu exportData={channelStats} headers={channelExportHeaders} filename="channel_analytics" />
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>{["Channel","Sent","Delivered","Clicked","Conv.","Revenue"].map((h,i) => (
                      <th key={h} className={`py-3 border-0 fw-semibold text-muted ${i===0?"ps-4":""} ${i>0?"text-center":""} ${i===5?"text-end pe-4":""}`}
                        style={{ fontSize:"0.72rem", textTransform:"uppercase" }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {channelStats.map((c,i) => (
                      <tr key={i} className="align-middle">
                        <td className="ps-4 fw-semibold">
                          {c.channel==="WhatsApp"&&<FaWhatsapp className="text-success me-2"/>}
                          {c.channel==="SMS"&&<FaSms className="text-secondary me-2"/>}
                          {c.channel==="Email"&&<FaEnvelope className="text-primary me-2"/>}
                          {c.channel}
                        </td>
                        <td className="text-center">{c.sent?.toLocaleString()}</td>
                        <td className="text-center"><span className="text-success fw-semibold">{c.delivered?.toLocaleString()}</span><div style={{fontSize:"0.68rem",color:"#6c757d"}}>{((c.delivered/c.sent)*100).toFixed(1)}%</div></td>
                        <td className="text-center"><span className="text-warning fw-semibold">{c.clicked?.toLocaleString()}</span><div style={{fontSize:"0.68rem",color:"#6c757d"}}>{((c.clicked/c.sent)*100).toFixed(1)}%</div></td>
                        <td className="text-center text-primary fw-semibold">{c.converted}</td>
                        <td className="text-end pe-4 fw-bold text-success">₹{c.revenue?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <h6 className="fw-bold mb-0"><i className="fas fa-route text-primary me-2"></i>Journey Performance</h6>
          <DownloadMenu exportData={journeyStats} headers={journeyExportHeaders} filename="journey_analytics" />
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>{["Journey","Trigger","Sent","CTR","Conv.","Revenue","Status"].map((h,i)=>(
                  <th key={h} className={`py-3 border-0 fw-semibold text-muted ${i===0?"ps-4":""} ${[2,3,4].includes(i)?"text-center":""} ${i===5?"text-end":""} ${i===6?"text-center pe-4":""}`}
                    style={{fontSize:"0.72rem",textTransform:"uppercase"}}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {journeyStats.map((j)=>(
                  <tr key={j.id} className="align-middle">
                    <td className="ps-4 fw-semibold">{j.name}</td>
                    <td><Badge bg="light" text="dark">{j.trigger}</Badge></td>
                    <td className="text-center">{j.sent?.toLocaleString()}</td>
                    <td className="text-center text-warning fw-semibold">{j.ctr}%</td>
                    <td className="text-center text-primary fw-semibold">{j.conversion}%</td>
                    <td className="text-end fw-bold text-success">₹{j.revenue?.toLocaleString()}</td>
                    <td className="text-center pe-4"><Badge bg={j.status==="Active"?"success":"secondary"} pill>{j.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      <style>{`.card{border-radius:12px!important}.card-header{border-radius:12px 12px 0 0!important}.page-header{margin-bottom:1.5rem}`}</style>
    </div>
  );
};
export default AnalyticsPage;
